import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { FEATURE_ROUTES } from "@/app/features";
import { createQueryClient } from "@/app/queryClient";
import { Layout } from "@/app/Layout";
import { RequireAuth } from "@/app/RequireAuth";
import { AccountsPage } from "@/pages/AccountsPage";
import { DeadLettersPage } from "@/pages/DeadLettersPage";
import { GlobalSettingsPage } from "@/pages/GlobalSettingsPage";
import { HealthPage } from "@/pages/HealthPage";
import { JobsPage } from "@/pages/JobsPage";
import { LoginPage } from "@/pages/LoginPage";
import { MappingDetailPage } from "@/pages/MappingDetailPage";
import { MappingsPage } from "@/pages/MappingsPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { SourcesPage } from "@/pages/SourcesPage";
import { Spinner, ToastProvider } from "@/ui";

// The UI kit showcase is only needed on /kit — keep it out of the main bundle.
const KitPage = lazy(() => import("@/pages/KitPage"));

/** Route table. Every route except /login sits behind RequireAuth inside the shell. */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<HealthPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="sources" element={<SourcesPage />} />
        <Route path="mappings" element={<MappingsPage />} />
        <Route path="mappings/:id" element={<MappingDetailPage />} />
        <Route path="settings" element={<GlobalSettingsPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="dead-letters" element={<DeadLettersPage />} />
        {FEATURE_ROUTES.map(({ id, path, navLabel, Page }) => (
          <Route
            key={id}
            path={path}
            element={
              <Suspense fallback={<Spinner label={`Loading ${navLabel.toLowerCase()}`} />}>
                <Page />
              </Suspense>
            }
          />
        ))}
        <Route
          path="kit"
          element={
            <Suspense fallback={<Spinner label="Loading UI kit" />}>
              <KitPage />
            </Suspense>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
